import { useState, useCallback } from 'react';
import { updateProfile as updateFirebaseProfile, auth } from '@municipality/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { message } from 'antd';
import type { User } from 'firebase/auth';
import type { ProfileUpdateData } from '../types/profile';

interface UseProfileReturn {
  updateUserProfile: (user: User, data: ProfileUpdateData, nameData?: { name: string; surname: string }) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const useProfile = (): UseProfileReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateUserProfile = useCallback(async (user: User, data: ProfileUpdateData, nameData?: { name: string; surname: string }) => {
    try {
      setLoading(true);
      setError(null);

      // Get the actual Firebase Auth user instead of the augmented user from context
      const firebaseAuthUser = auth.currentUser;
      if (!firebaseAuthUser) {
        throw new Error('No authenticated user found');
      }

      // Update Firebase Auth profile
      if (data.displayName) {
        await updateFirebaseProfile(firebaseAuthUser, {
          displayName: data.displayName
        });
      }

      // Update Firestore user document
      const userDocRef = doc(db, 'users', user.uid);
      const updateData: Record<string, string> = {
        updatedAt: new Date().toISOString()
      };

      if (data.displayName) updateData.displayName = data.displayName;
      if (data.fullName) updateData.fullName = data.fullName;
      if (nameData?.name) updateData.name = nameData.name;
      if (nameData?.surname) updateData.surname = nameData.surname;

      await updateDoc(userDocRef, updateData);
      
      message.success('Profile updated successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
      message.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateUserProfile,
    loading,
    error
  };
};
